import { useState, useEffect } from 'react';
import { Outlet, Link } from 'react-router-dom';
import { useIncidentStore } from '@/store/incident.store';
import { useTenantQuery } from '@/hooks/useTenantQuery';
import { api } from '@/services/api';
import { Incident, IncidentStatus, STATUS_COLORS } from '@/types/incident';

import Button from '../common/Button';
import FaIcon from '../common/FaIcon';

const STATUS_LABELS: Record<IncidentStatus, string> = {
  planned: 'Planned',
  active: 'Active',
  stabilizing: 'Stabilizing',
  recovery: 'Recovery',
  closed: 'Closed',
  archived: 'Archived',
};

/**
 * Route guard that requires an active incident to be engaged.
 * Shows a message prompting the user to select an incident if none is engaged,
 * along with a list of recent active incidents for quick selection.
 */
export function RequireIncident() {
  const { activeIncident, activeIncidentId, isDailyOps, _hasHydrated, setActiveIncident } = useIncidentStore();

  // Timeout fallback: if hydration hasn't completed within 2 seconds, proceed anyway.
  // This prevents permanent spinner if onRehydrateStorage fails to fire.
  const [hydrationTimedOut, setHydrationTimedOut] = useState(false);
  useEffect(() => {
    if (_hasHydrated) return;
    const timer = setTimeout(() => setHydrationTimedOut(true), 2000);
    return () => clearTimeout(timer);
  }, [_hasHydrated]);

  const effectivelyHydrated = _hasHydrated || hydrationTimedOut;
  const needsIncident = !activeIncident || isDailyOps;

  // All hooks must be called before any conditional returns
  const { data: incidentResponse, isLoading } = useTenantQuery<{ data: Incident[] }>(
    ['incidents', 'require-incident-guard'],
    '/incidents',
    {
      params: { status: 'active,stabilizing,recovery,planned', limit: '5' },
      enabled: needsIncident && effectivelyHydrated,
    }
  );
  const recentIncidents = incidentResponse?.data ?? [];

  const handleEngageIncident = (incident: Incident) => {
    setActiveIncident(incident);
    // Persist to the server, exactly as IncidentSwitcher does. user_contexts is the
    // durable record of the engaged incident and is now reconciled on boot, so an engage
    // that only touches the local store is dropped on the next page load — and, before
    // that reconciliation existed, simply never reached the user's other devices.
    api.put('/user/context', {
      incident_id: incident.id,
      is_daily_ops: false,
    }).catch(console.error);
  };

  // Wait for zustand to rehydrate from localStorage before making decisions
  if (!effectivelyHydrated) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-primary-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  // Still rehydrating persisted incident from server after page reload
  if (activeIncidentId && !activeIncident && !isDailyOps) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-primary-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  // If user has an engaged incident, render the protected content
  if (activeIncident && !isDailyOps) {
    return <Outlet />;
  }

  // Otherwise, show a message asking them to select an incident
  return (
    <div className="flex-1 flex items-center justify-center p-8">
      <div className="max-w-lg w-full">
        <div className="text-center mb-8">
          <div className="mx-auto w-16 h-16 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center mb-6">
            <FaIcon icon="triangle-exclamation" className="w-8 h-8 text-amber-600 dark:text-amber-500" />
          </div>
          {/*
            This screen replaces the whole page on every incident-scoped route,
            so it is the page — and it had no <h1>. An operator landing here from
            /tasks, /pods, /damage-assessments or /supply-requests got a document
            whose outline started at h2 with nothing above it.
          */}
          <h1 className="heading-2 mb-2">
            No Incident Selected
          </h1>
          <p className="text-theme-secondary">
            This feature requires an active incident. Select one below or view all incidents.
          </p>
        </div>

        {/* Recent incidents list */}
        {isLoading ? (
          <div className="space-y-3 mb-6">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="animate-pulse bg-gray-100 dark:bg-gray-800 rounded-lg h-16" />
            ))}
          </div>
        ) : recentIncidents.length > 0 ? (
          <div className="mb-6">
            <h3 className="text-sm font-medium text-theme-tertiary mb-3">
              Recent Incidents
            </h3>
            <div className="space-y-2">
              {recentIncidents.map((incident) => (
                <Button
                  key={incident.id}
                  variant="outline"
                  onClick={() => handleEngageIncident(incident)}
                  className="w-full text-left p-3 h-auto justify-start"
                >
                  <div className="flex items-center justify-between">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-theme-primary truncate">
                        {incident.name}
                      </p>
                      <p className="font-mono text-xs text-theme-tertiary mt-0.5">
                        {incident.incident_number}
                      </p>
                    </div>
                    <span className={`ml-3 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[incident.status].bg} ${STATUS_COLORS[incident.status].text}`}>
                      {STATUS_LABELS[incident.status]}
                    </span>
                  </div>
                </Button>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center mb-6 py-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-theme-primary">
            <p className="text-sm text-theme-tertiary mb-3">
              No active incidents found.
            </p>
            <Link
              to="/incidents/new"
              className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 dark:focus:ring-offset-gray-900"
            >
              Create Incident
            </Link>
          </div>
        )}

        <div className="text-center">
          <Link
            to="/incidents"
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-gray-900"
          >
            View All Incidents
          </Link>
        </div>
      </div>
    </div>
  );
}
